<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <v-card flat class="my-9 mx-5 pa-9 rounded-xl">
                    <v-card-title class="card-title px-0 pt-0">{{formatMenuText($t('reports.menu'))}}</v-card-title>
                    <v-card-text>
                        <h3 class="mt-4">{{formatMenuText($t('reports.types'))}}</h3>
                        <v-radio-group :row="!$vuetify.breakpoint.xsOnly" v-model="reportType">
                            <v-radio v-for="rt in reportTypes"
                                     class="ma-2"
                                     :key="`types_${rt.type}`"
                                     :label="rt.label"
                                     :value="rt.type"></v-radio>
                        </v-radio-group>
                        <h3 class="my-4">{{formatMenuText($t('reports.filters'))}}</h3>
                        <v-form v-model="isValid" ref="form">
                            <v-row align="center" class="my-4">
                                <v-col cols="12" sm="6" md="4" class="pa-2" v-if="user.role==='SUPER_USER'">
                                    <v-select ref="clientSelect"
                                              :label="formatMenuText($t('reports.client'))"
                                              class="hand"
                                              v-model="clientIds"
                                              :items="clients"
                                              item-text="Name"
                                              item-value="Id"
                                              :rules="[rules.required]"
                                              :disabled="loading"
                                              dense outlined hide-details multiple
                                              @change="onClientChange" />
                                </v-col>
                                <v-col v-if="reportType === 'connection' || reportType === 'newUsers' || reportType === 'documentAccess'"
                                       cols="12" sm="6" md="4"
                                       class="pa-2">
                                    <DatePicker v-model="searchDates"
                                                :minDate="searchDateMin"
                                                :locale="$i18n.locale"
                                                :error="dateFromToInvalid"
                                                @input="onDateChange"></DatePicker>
                                </v-col>
                                <v-col v-if="reportType === 'connection' || reportType === 'users'"
                                       cols="12" sm="6" md="4"
                                       class="pa-2">
                                    <v-text-field v-model="firstNameContains"
                                                  dense outlined hide-details
                                                  maxlength="100"
                                                  :label="formatMenuText($t('reports.firstNameContains'))"
                                                  :rules="rules.firstnameRules"
                                                  :disabled="loading"></v-text-field>
                                </v-col>
                                <v-col v-if="reportType === 'connection' || reportType === 'users'"
                                       cols="12" sm="6" md="4"
                                       class="pa-2">
                                    <v-text-field v-model="lastNameContains"
                                                  dense outlined hide-details
                                                  maxlength="100"
                                                  :label="formatMenuText($t('reports.lastNameContains'))"
                                                  :rules="rules.lastnameRules"
                                                  :disabled="loading"></v-text-field>
                                </v-col>
                                <v-col v-if="reportType === 'connection' || reportType === 'users'"
                                       cols="12" sm="6" md="4"
                                       class="pa-2">
                                    <v-text-field v-model="emailContains"
                                                  dense outlined hide-details
                                                  maxlength="255"
                                                  :label="formatMenuText($t('reports.emailContains'))"
                                                  :rules="rules.emailRules"
                                                  :disabled="loading"></v-text-field>
                                </v-col>
                                <v-col v-if="reportType === 'orphans'"
                                       cols="12" sm="6" md="4"
                                       class="pa-2">
                                    <v-select :label="formatMenuText($t('reports.definition'))"
                                              class="hand"
                                              v-model="definitionId"
                                              :items="definitionsByClient"
                                              item-text="Title"
                                              item-value="Id"
                                              :disabled="loading"
                                              dense outlined hide-details
                                              @change="onDefinitionChange" />
                                </v-col>
                            </v-row>
                        </v-form>
                        <v-row class="my-4">
                            <v-spacer class="d-none d-sm-flex"></v-spacer>
                            <v-col cols="12" sm="auto" class="pa-2">
                                <v-btn color="primary"
                                       elevation="0"
                                       block
                                       @click="generate"
                                       :disabled="!validated">
                                    <v-icon class="mr-2">
                                        mdi-download
                                    </v-icon>
                                    <span class="mr-2">{{formatMenuText($t('reports.generate'))}}</span>
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <!--Download Dialog-->
        <v-dialog v-model="downloading" max-width="500px">
            <v-card class="pa-8 rounded-xl" flat>
                <v-row align="center" justify="center">
                    <v-icon size="96px"
                            color="primary"
                            class="my-5">mdi-chart-bar</v-icon>
                </v-row>
                <v-card-subtitle class="text-center card-subtitle font-weight-medium mt-1">{{ $t('reports.download-title') }}</v-card-subtitle>
                <v-card-text>
                    <v-row class="ma-4">
                        <v-col class="text-center text-normal" cols="12">
                            <span v-if="!errorMessage">{{ $t('reports.download-text') }}</span>
                            <span v-if="errorMessage">{{ $t(errorMessage) }}</span>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="text-center" cols="12"><v-progress-circular v-if="!errorMessage" indeterminate color="primary" /></v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-row justify="center">
                        <v-btn class="text-normal"
                               depressed
                               color="primary"
                               width="33%"
                               x-large
                               @click="cancelDownload">
                            {{errorMessage ? $t('ok') : $t('cancel')}}
                        </v-btn>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<style scoped>
    /deep/ .v-input--selection-controls__input {
        margin-right: 0 !important;
    }

    .tight--row--top,
    .tight--row--bottom {
        margin: -12px -12px;
    }

    .max-input-width {
        max-width: 300px;
    }

    @media (max-width: 600px) {
        .max-input-width {
            max-width: 100%;
        }
    }
</style>
<script>
    import { mapState } from "vuex";
    //import DownloadFrame from '../../components/downloadFrame';
    import DatePicker from '../../components/datePicker.vue';

    export default {
        name: 'Reports',
        components: { DatePicker },
        data: () => ({
            loading: false,
            downloading: false,
            currentRoute: null,
            reportType: 'connection',
            searchDateMin: null,
            searchDates: [],
            clientIds: [],
            clients: [],
            definitionId: null,
            definitions: [],
            isValid: false,
            firstNameContains: undefined,
            lastNameContains: undefined,
            emailContains: undefined,
            errorMessage: null
        }),
        computed: {
            ...mapState("account", ["user"]),
            validated() {
                return this.isValid;
            },
            rules() {
                return {
                    required: v => v && v.toString().length > 0 || false,
                    emailRules: [
                        v => !v || (v && v.length <= 256) || false
                    ],
                    firstnameRules: [
                        v => !v || (v && v.length <= 255) || false
                    ],
                    lastnameRules: [
                        v => !v || (v && v.length <= 255) || false
                    ]
                }
            },
            definitionsByClient() {
                if (this.definitions == null || this.clientId == null) return undefined;
                return this.definitions.filter(x => x.ClientId === this.clientId);
            },
            dateFromToInvalid() {
                if (!this.searchDates || this.searchDates.length !== 2)
                    return false;

                return this.checkDateDiff() > 3; // Allow maximum 3 months interval for dates
            },
            reportTypes() {
                return [
                    { label: this.formatMenuText(this.$t('reports.connection')), type: 'connection' },
                    { label: this.formatMenuText(this.$t('reports.users')), type: 'users' },
                    { label: this.formatMenuText(this.$t('reports.newUsers')), type: 'newUsers' },
                    { label: this.formatMenuText(this.$t('reports.documentAccess')), type: 'documentAccess' },
                    //{ label: this.formatMenuText(this.$t('reports.orphans')), type: 'orphans' }
                ]
            }
        },
        created() {
            const curr = new Date(),
                first = new Date();

            first.setDate(first.getDate() - 7);
            this.searchDates[0] = first.getUTCFullYear() + '-' + (first.getUTCMonth() + 1).toString().padStart(2, '0') + '-' + first.getUTCDate().toString().padStart(2, '0');
            this.searchDates[1] = curr.getUTCFullYear() + '-' + (curr.getUTCMonth() + 1).toString().padStart(2, '0') + '-' + curr.getUTCDate().toString().padStart(2, '0');

            this.currentRoute = this.$router.currentRoute;
            this.reload();
        },
        mounted() {
            this.$refs.form.validate();
        },
        watch: {
            "$route"() {
                this.currentRoute = this.$router.currentRoute;
            },
        },
        methods: {
            formatMenuText(text) {
                if (!text) return text;

                // Check the titleCase setting from the current experience
                const titleCase = this.$experience.titleCase !== false; // default to true if not set

                if (titleCase) {
                    // Title case: capitalize first letter of all words
                    return text.split(' ').map(word => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        return word.charAt(0).toUpperCase() + word.slice(1);
                    }).join(' ');
                } else {
                    // Sentence case: only capitalize first letter of first word
                    // But preserve acronyms
                    const words = text.split(' ');
                    return words.map((word, index) => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        if (index === 0) {
                            // First word: capitalize first letter
                            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                        } else {
                            // Other words: all lowercase
                            return word.toLowerCase();
                        }
                    }).join(' ');
                }
            },
            checkDateDiff() {
                let dateFrom = this.searchDates[0].split('-');
                let dateTo = this.searchDates[1].split('-');
                let d1 = new Date(dateFrom[0], dateFrom[1], dateFrom[2]),
                    d2 = new Date(dateTo[0], dateTo[1], dateTo[2]);
                let months = (d2.getFullYear() - d1.getFullYear()) * 12;
                months -= d1.getMonth();
                months += d2.getMonth();
                return months <= 0 ? 0 : months;
            },
            reload() {
                this.fetchClients().then(() => {
                    this.fetchDefinitions();
                });
            },
            fetchClients() {
                const self = this;
                this.loading = true;
                return self.$axios.get('/reports/clients')
                    .then((response) => {
                        if (response.data.success == false && response.data.error == 'session-expired') {
                            self.$router.push({ name: 'login' });
                        } 
                        self.clients = response.data;
                        self.clientIds = [self.clients[0].Id];
                    })
                    .catch(() => {
                        //self.inError = true;
                    })
                    .then(() => {
                        self.loading = false;
                    });
            },
            fetchDefinitions() {
                const self = this;
                this.loading = true;
                return self.$axios.get('/reports/definitions')
                    .then((resp) => {
                        self.definitions = resp.data;
                        self.definitionId = self.definitions[0].Id;
                    })
                    .catch(() => {
                        //self.inError = true;
                    })
                    .then(() => {
                        self.loading = false;
                    });
            },
            onClientChange() {
                this.$refs.form.validate();
            },
            onDefinitionChange() {
                this.$refs.form.validate();
            },
            onDateChange() {
                this.$refs.form.validate();
            },
            generate() {
                const self = this;
                self.downloading = true;
                self.errorMessage = null;
                const request = {
                    clientIds: this.clientIds,
                    dateFrom: this.searchDates[0],
                    dateTo: this.searchDates[1],
                    type: this.reportType,
                    firstNameContains: this.firstNameContains === undefined ? '' : this.firstNameContains,
                    lastNameContains: this.lastNameContains === undefined ? '' : this.lastNameContains,
                    emailContains: this.emailContains === undefined ? '' : this.emailContains,
                    timeZoneOffset: new Date().getTimezoneOffset()
                };
                return self.$axios.post('/reports/generate', request, { responseType: 'blob'})
                    .then((response) => {
                        if (response.data.success == false && response.data.error == 'session-expired') {
                            self.$router.push({ name: 'login' });
                        } 
                        const type = response.headers['content-type'] || null
                        const relType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

                        if (type.includes(relType)) {
                            
                            self.downloading = false;
                            const link = document.createElement('a');

                            link.href = window.URL.createObjectURL(
                                new Blob([response.data])
                            );
                            let filename = self.$t(`reports.filename-${request.type}`);
                            link.setAttribute('download', filename);
                            link.click();

                            return;
                        }

                        if (type.includes('application/json')) {
                            let reader = new FileReader()
                            reader.onload = e => {
                                if (e.target.readyState === 2) {
                                    let res = {}
                                    res = JSON.parse(e.target.result)
                                    if (res.error)
                                        self.errorMessage = res.error;
                                }
                            }
                            reader.readAsText(response.data)
                        }
                        
                            
                    })
                    .catch((error) => {
                        console.log(`Error generate: ${error}`);
                    })
            },
            cancelDownload() {
                this.errorMessage = null;
                this.downloading = false;
            },
        }
    }
</script>